import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { normalizePropertyKey } from "../utils/roomRateCombos";

/**
 * Custom hook for managing room/rate plan selection state and logic
 * @param {Object} params
 * @param {Object} params.promo - The promo object containing allowedRateRoomCodes
 * @param {Array} params.rooms - Array of room objects
 * @param {Array} params.ratePlans - Array of rate plan objects
 * @param {string|number|null} params.dataPropertyId - Property the rooms/rate
 *   plans belong to. Stamped on every combo the user selects so the payload
 *   mappers can reject a selection made against a different property.
 * @param {Function} params.onSelectionChange - Callback when selection changes
 */
export function useRoomsRateplansSelection({
  promo,
  rooms,
  ratePlans,
  dataPropertyId = null,
  onSelectionChange,
}) {
  const comboPropertyId = normalizePropertyKey(dataPropertyId);
  // Memoize selectedList to prevent creating new array reference on every render
  const selectedList = useMemo(
    () => promo?.allowedRateRoomCodes || [],
    [promo?.allowedRateRoomCodes]
  );

  // Memoize getSelectedMapping function (defined early since it's used in initialSelectedValues)
  const getSelectedMapping = useCallback((selectedCombo) => {
    return selectedCombo.reduce(
      (acc, c) => {
        if (!acc.roomToRateplan[c.mRight]) {
          acc.roomToRateplan[c.mRight] = [];
        }
        if (!acc.roomToRateplan[c.mRight].includes(c.mLeft)) {
          acc.roomToRateplan[c.mRight].push(c.mLeft);
        }
        if (!acc.rateToRoom[c.mLeft]) {
          acc.rateToRoom[c.mLeft] = [];
        }
        if (!acc.rateToRoom[c.mLeft].includes(c.mRight)) {
          acc.rateToRoom[c.mLeft].push(c.mRight);
        }
        return acc;
      },
      { roomToRateplan: {}, rateToRoom: {} }
    );
  }, []);

  // Memoize mapping to avoid recalculating on every render
  const mapping = useMemo(() => {
    const map = { allRoomRateplans: [], roomToRateplan: {}, rateToRoom: {} };
    rooms.forEach((room) => {
      map.allRoomRateplans.push(room.code);
      map.roomToRateplan[room.code] = [];
      ratePlans.forEach((rp) => {
        if (!map.allRoomRateplans.includes(rp.code)) {
          map.allRoomRateplans.push(rp.code);
        }
        map.roomToRateplan[room.code].push(rp.code);
        if (!map.rateToRoom[rp.code]) {
          map.rateToRoom[rp.code] = [];
        }
        map.rateToRoom[rp.code].push(room.code);
      });
    });
    return map;
  }, [rooms, ratePlans]);

  const [selectedCombo, setSelectedCombo] = useState([...selectedList]);

  // Derive header selection states from selectedCombo (single source of truth)
  const selectedRateplan = useMemo(() => {
    if (selectedCombo.length === 0) return [];
    const selectedMapping = getSelectedMapping(selectedCombo);
    return ratePlans
      .map((rp) => rp.code)
      .filter(
        (rpCode) =>
          selectedMapping.rateToRoom[rpCode]?.length ===
          mapping.rateToRoom[rpCode]?.length
      );
  }, [selectedCombo, ratePlans, mapping, getSelectedMapping]);

  const selectedRooms = useMemo(() => {
    if (selectedCombo.length === 0) return [];
    const selectedMapping = getSelectedMapping(selectedCombo);
    return rooms
      .map((room) => room.code)
      .filter(
        (roomCode) =>
          selectedMapping.roomToRateplan[roomCode]?.length ===
          mapping.roomToRateplan[roomCode]?.length
      );
  }, [selectedCombo, rooms, mapping, getSelectedMapping]);

  // Use refs to track previous values and prevent infinite loops
  const prevSelectedComboRef = useRef(JSON.stringify(selectedCombo));
  const prevSelectedListRef = useRef(JSON.stringify(selectedList));
  const selectedComboRef = useRef(selectedCombo);

  // Update refs when state changes
  useEffect(() => {
    selectedComboRef.current = selectedCombo;
  }, [selectedCombo]);

  // Sync selectedCombo when selectedList changes (e.g., when drawer reopens with saved selections)
  useEffect(() => {
    const currentSelectedListKey = JSON.stringify(selectedList);
    // Only update if selectedList prop actually changed
    if (prevSelectedListRef.current !== currentSelectedListKey) {
      prevSelectedListRef.current = currentSelectedListKey;
      // Only reset local state if it differs from the new prop value
      if (JSON.stringify(selectedComboRef.current) !== currentSelectedListKey) {
        setSelectedCombo([...selectedList]);
      }
    }
  }, [selectedList]);

  // Memoize getStatus function
  const getStatus = useCallback(
    (roomCode, rateplanCode) => {
      return selectedCombo.some(
        (combo) => combo.mRight === roomCode && combo.mLeft === rateplanCode
      );
    },
    [selectedCombo]
  );

  useEffect(() => {
    const currentComboKey = JSON.stringify(selectedCombo);
    // Only notify parent if selectedCombo actually changed
    if (prevSelectedComboRef.current !== currentComboKey) {
      prevSelectedComboRef.current = currentComboKey;
      if (onSelectionChange) {
        onSelectionChange([...selectedCombo]);
      }
    }
  }, [selectedCombo, onSelectionChange]);

  const toggleCell = useCallback(
    (cellData) => {
      const { roomCode, rateCode } = cellData;

      setSelectedCombo((prev) => {
        const isCurrentlySelected = prev.some(
          (combo) => combo.mRight === roomCode && combo.mLeft === rateCode
        );

        if (isCurrentlySelected) {
          return prev.filter(
            (combo) => !(combo.mRight === roomCode && combo.mLeft === rateCode)
          );
        } else {
          const rp = ratePlans.find((r) => r.code === rateCode);
          const room = rooms.find((r) => r.code === roomCode);
          return [
            ...prev,
            {
              mRight: roomCode,
              mLeft: rateCode,
              rateplanId: rp?.id,
              roomtypeId: room?.id,
              propertyId: comboPropertyId,
            },
          ];
        }
      });
    },
    [ratePlans, rooms, comboPropertyId]
  );

  const selectAllRoomsForRateplan = useCallback(
    (rateCode) => {
      const rp = ratePlans.find((r) => r.code === rateCode);
      const combos = rooms.map((room) => ({
        mRight: room.code,
        mLeft: rateCode,
        rateplanId: rp?.id,
        roomtypeId: room.id,
        propertyId: comboPropertyId,
      }));
      const isSelected = selectedRateplan.includes(rateCode);

      setSelectedCombo((prev) => {
        const withoutThisRate = prev.filter(
          (combo) => combo.mLeft !== rateCode
        );
        return isSelected ? withoutThisRate : [...withoutThisRate, ...combos];
      });
    },
    [rooms, ratePlans, selectedRateplan, comboPropertyId]
  );

  const selectAllRPForRoom = useCallback(
    (roomCode) => {
      const room = rooms.find((r) => r.code === roomCode);
      const combos = ratePlans.map((rateplan) => ({
        mRight: roomCode,
        mLeft: rateplan.code,
        rateplanId: rateplan.id,
        roomtypeId: room?.id,
        propertyId: comboPropertyId,
      }));
      const isSelected = selectedRooms.includes(roomCode);

      setSelectedCombo((prev) => {
        const withoutThisRoom = prev.filter(
          (combo) => combo.mRight !== roomCode
        );
        return isSelected ? withoutThisRoom : [...withoutThisRoom, ...combos];
      });
    },
    [rooms, ratePlans, selectedRooms, comboPropertyId]
  );

  // Calculate selectedRoomsAndRPs from current state
  const selectedRoomsAndRPs = useMemo(() => {
    return [...selectedRateplan, ...selectedRooms];
  }, [selectedRateplan, selectedRooms]);

  const selectAllRPAndRooms = useCallback(() => {
    const isAllSelected =
      selectedRoomsAndRPs.length === mapping.allRoomRateplans.length;

    if (isAllSelected) {
      setSelectedCombo([]);
      return;
    }

    const allCombos = [];
    rooms.forEach((room) => {
      ratePlans.forEach((rp) => {
        allCombos.push({
          mLeft: rp.code,
          mRight: room.code,
          rateplanId: rp.id,
          roomtypeId: room.id,
          propertyId: comboPropertyId,
        });
      });
    });
    setSelectedCombo(allCombos);
  }, [
    rooms,
    ratePlans,
    selectedRoomsAndRPs,
    mapping.allRoomRateplans.length,
    comboPropertyId,
  ]);

  return {
    // State
    selectedCombo,
    selectedRateplan,
    selectedRooms,
    selectedRoomsAndRPs,
    mapping,
    // Functions
    getStatus,
    toggleCell,
    selectAllRoomsForRateplan,
    selectAllRPForRoom,
    selectAllRPAndRooms,
  };
}
